import React, { createContext, useContext, useState, useEffect } from "react";
import apiService from "../services/apiService";
import SessionManager from "../utils/SessionManager";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has a valid session using SessionManager
    if (SessionManager.hasValidSession()) {
      const session = SessionManager.getSession();
      if (session && session.userData) {
      setIsAuthenticated(true);
        setUser(session.userData);
      } else {
        // Session exists but data is invalid, clear it
        SessionManager.clearSession();
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      // No valid session, ensure everything is cleared
      SessionManager.clearSession();
      setIsAuthenticated(false);
      setUser(null);
    }

    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // Use the PHP API for authentication
      const result = await apiService.login(email, password);

      if (result.success) {
        // LoginEduokCrm.php returns `staff` array when successful
        const staffArray = result.data && result.data.staff ? result.data.staff : [];
        const staff = staffArray[0] || null;

        // Fallback to entire result if structure differs
        const userData = staff || result.data || {};

        // Extract user role/login type from response
        // Check for common field names: login_type, user_type, role, staff_type, etc.
        const loginType = userData.login_type || 
                         userData.user_type || 
                         userData.role || 
                         userData.staff_type ||
                         userData.type ||
                         'ADMIN'; // Default to ADMIN if not found

        // Normalize login type to uppercase
        const normalizedLoginType = loginType.toUpperCase();

        // Add loginType to userData for easy access
        const userDataWithRole = {
          ...userData,
          loginType: normalizedLoginType,
          role: normalizedLoginType,
        };

        const token = "php-api-token-" + Date.now();

        // Store session using SessionManager
        SessionManager.setSession(token, userDataWithRole);

        setIsAuthenticated(true);
        setUser(userDataWithRole);

        // Call getCrmAdminDashboard.php only to create/establish session for ADMIN
        if (normalizedLoginType === 'ADMIN') {
          try {
            const dashboardResult = await apiService.getCrmAdminDashboard();
            if (dashboardResult.success) {
              console.log("[AuthContext] Admin dashboard session established:", dashboardResult.data);
              // API called only for session creation, not storing dashboard data
            } else {
              console.warn("[AuthContext] Failed to establish admin dashboard session:", dashboardResult.error);
            }
          } catch (error) {
            console.error("[AuthContext] Error establishing admin dashboard session:", error);
            // Don't fail login if session establishment fails
          }
        }

        return { success: true, user: userDataWithRole };
      } else {
        return { success: false, error: result.error || "Login failed" };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Network error occurred",
      };
    }
  };

  const logout = () => {
    SessionManager.clearSession();
    setIsAuthenticated(false);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    SessionManager.setUserData(userData);
  };

  // Helper function to get user role
  const getUserRole = () => {
    if (!user) return null;
    return user.loginType || user.role || 'ADMIN';
  };

  // Helper functions to check user role
  const isAdmin = () => getUserRole() === 'ADMIN' || getUserRole() === 'Admin';
  const isTL = () => getUserRole() === 'TL';
  const isTrainer = () => getUserRole() === 'TRAINER';

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    updateUser,
    getUserRole,
    isAdmin,
    isTL,
    isTrainer,
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.2rem",
          color: "#666",
        }}
      >
        Loading...
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
